import React from 'react';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { useLocation } from '@reach/router';
import styled from '@emotion/styled';
import Cookies from 'js-cookie';

import useUserContext from '../../hooks/use-user-context';
import { createOptionMap, mergeQueryParams } from '../../utilities/misc';
import { removeLanguageFromPath, generateLanguagePath } from '../../utilities/url';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { fontWeights } from '../../theme/type';
import { LANGUAGES, CN_SITE_URL, SITE_URL, JP_SITE_URL, KR_SITE_URL, TH_SITE_URL, ID_SITE_URL } from '../../constants';

import Dropdown from '../dropdown/Dropdown';
import DropdownFocusWrapper from '../dropdown/DropdownFocusWrapper';
import DropdownPanel from '../dropdown/DropdownPanel';
import { ReactComponent as IconCaretDown } from '../../images/icons/caret-down.svg';
import DropdownMenuButtons from '../dropdown/DropdownMenuButtons';

const languageItems = Object.keys(LANGUAGES).map((language) => ({
  id: language,
  title: LANGUAGES[language].name
}));

const DropdownStyled = styled(Dropdown)`
  display: flex;
  font-size: ${rem(14)};
  position: relative;
`;

const IconCaretStyled = styled(IconCaretDown)`
  margin-left: ${rem(8)};
  flex-shrink: 0;

  ${DropdownStyled}:focus-within &,
  ${DropdownStyled}:hover & {
    transform: rotate(180deg);
  }
`;

const ToggleButton = styled.button`
  border: none;
  background: none;
  color: ${colors.blue['800']};
  font-weight: ${fontWeights.semiBold};
  display: flex;
  height: ${rem(90)};
  align-items: center;
  cursor: pointer;

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}

  &:hover {
    &::after {
      content: '';
      position: absolute;
      z-index: 1;
      inset: calc(50% + ${rem(8)}) ${rem(16)} auto;
      height: ${rem(10)};
      transform-origin: top;
      transform: perspective(${rem(8)}) rotateX(45deg);
    }
  }
`;

const NavbarLanguageSwitcher = ({ setIsDropdownOpen }) => {
  const data = useStaticQuery(graphql`
    query NavbarLanguageSwitcher {
      allContentfulOptionSet(filter: {name: {eq: "navbar"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const { pathname } = useLocation();
  const { language, setLanguage } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);

  const changeLanguage = (languageItem) => {
    const newLanguage = languageItem.id;
    const cleanedPath = removeLanguageFromPath(pathname);
    const userContextCookieData = Cookies.get('userContext')
      ? JSON.parse(Cookies.get('userContext')) : {};

    if (newLanguage === 'jp') {
      let newUrl = mergeQueryParams(JP_SITE_URL, {
        ...userContextCookieData,
        page: '', // Disable value for page to not break Japan site, which changes based on page param
      });
      if ('addCrossDomainParams' in window) newUrl = window.addCrossDomainParams(newUrl);
      window.location.href = newUrl;
      return;
    }

    if (newLanguage === 'kr') {
      let newUrl = mergeQueryParams(KR_SITE_URL, userContextCookieData);
      if ('addCrossDomainParams' in window) newUrl = window.addCrossDomainParams(newUrl);
      window.location.href = newUrl;
      return;
    }

    if (newLanguage === 'th') {
      let newUrl = mergeQueryParams(TH_SITE_URL, userContextCookieData);
      if ('addCrossDomainParams' in window) newUrl = window.addCrossDomainParams(newUrl);
      window.location.href = newUrl;
      return;
    }

    if (newLanguage === 'id') {
      let newUrl = mergeQueryParams(ID_SITE_URL, userContextCookieData);
      if ('addCrossDomainParams' in window) newUrl = window.addCrossDomainParams(newUrl);
      window.location.href = newUrl;
      return;
    }

    if (newLanguage === 'zh-Hans') {
      let newUrl = `${CN_SITE_URL}${generateLanguagePath(newLanguage, cleanedPath)}`;
      newUrl = mergeQueryParams(newUrl, userContextCookieData);
      if ('addCrossDomainParams' in window) newUrl = window.addCrossDomainParams(newUrl);
      window.location.href = newUrl;
      return;
    }

    if (language === 'zh-Hans') {
      let newUrl = `${SITE_URL}${generateLanguagePath(newLanguage, cleanedPath)}`;
      newUrl = mergeQueryParams(newUrl, userContextCookieData);
      if ('addCrossDomainParams' in window) newUrl = window.addCrossDomainParams(newUrl);
      window.location.href = newUrl;
      return;
    }

    const newPath = generateLanguagePath(newLanguage, cleanedPath);

    setLanguage(newLanguage);
    navigate(newPath);
  };

  return (
    <DropdownStyled>
      <DropdownFocusWrapper
        hasDropdown={languageItems && languageItems.length > 0}
        setIsDropdownOpen={setIsDropdownOpen}
      >
        <ToggleButton type="button">
          {language ? LANGUAGES[language].name : null}
          <IconCaretStyled />
        </ToggleButton>
        <DropdownPanel
          label={options['navbar.language_switcher_label']}
        >
          <DropdownMenuButtons
            items={languageItems}
            onClick={changeLanguage}
          />
        </DropdownPanel>
      </DropdownFocusWrapper>
    </DropdownStyled>
  );
};

export default NavbarLanguageSwitcher;
